import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { portalFeaturesForSession } from '../../services/portal/portalFeatures.service';
import { formatDate } from '../../utils/dateFormat';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';

// Same toolbar as the typing-center pages: one 32px row.
const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

const STATUS_CONFIG = {
  PENDING_REVIEW: { color: 'secondary', label: 'Pending Review' },
  OPEN: { color: 'info', label: 'Open' },
  IN_PROGRESS: { color: 'warning', label: 'In Progress' },
  COMPLETED: { color: 'success', label: 'Completed' },
  PARTIALLY_INVOICED: { color: 'info', label: 'Partly invoiced' },
  INVOICED: { color: 'primary', label: 'Invoiced' },
  CANCELLED: { color: 'error', label: 'Cancelled' }
};

const StatusChip = ({ status }) => {
  const config = STATUS_CONFIG[status] || { color: 'default', label: status };
  return <Chip label={config.label} color={config.color} size="small" />;
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-AE', {
  style: 'currency',
  currency: 'AED'
}).format(parseFloat(amount) || 0);

// Handles camelCase and snake_case from the backend.
const getField = (obj, camelCase, snakeCase) => obj[camelCase] || obj[snakeCase] || null;

const PortalWorkOrders = ({ portalUser }) => {
  const canCreate = portalFeaturesForSession(portalUser).CREATE_WORK_ORDER;
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchWorkOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getPortalWorkOrders();
      setWorkOrders(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch work orders:', err);
      setError('Failed to load work orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  const counts = useMemo(() => {
    const by = (s) => workOrders.filter((wo) => wo.status === s).length;
    return {
      total: workOrders.length,
      OPEN: by('OPEN'),
      IN_PROGRESS: by('IN_PROGRESS'),
      COMPLETED: by('COMPLETED'),
      INVOICED: by('INVOICED') + by('PARTIALLY_INVOICED')
    };
  }, [workOrders]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return workOrders.filter((wo) => {
      if (statusFilter && wo.status !== statusFilter) return false;
      if (!term) return true;
      const woNumber = wo.workOrderNumber || wo.work_order_number || '';
      return woNumber.toLowerCase().includes(term) || (wo.title || '').toLowerCase().includes(term);
    });
  }, [workOrders, searchTerm, statusFilter]);

  useEffect(() => { setPage(0); }, [searchTerm, statusFilter]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const open = (wo) => navigate(`/portal/work-orders/${wo.id}`);

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button size="small" onClick={fetchWorkOrders} sx={{ textTransform: 'none' }}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      <ProTableStats
        loading={loading}
        cards={[
          { key: '', label: 'Total', value: counts.total },
          { key: 'OPEN', label: 'Open', value: counts.OPEN, color: 'info.main' },
          { key: 'IN_PROGRESS', label: 'In Progress', value: counts.IN_PROGRESS, color: 'warning.main' },
          { key: 'COMPLETED', label: 'Completed', value: counts.COMPLETED, color: 'success.main' },
          { key: 'INVOICED', label: 'Invoiced', value: counts.INVOICED, color: 'primary.main' },
        ].map((c) => ({
          ...c,
          highlight: statusFilter === c.key,
          onClick: () => setStatusFilter(c.key),
        }))}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
        <TextField
          size="small"
          placeholder="Search by number or title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: '1 1 200px', minWidth: 200, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
            <MenuItem value="PENDING_REVIEW" sx={{ fontSize: '0.8125rem' }}>Pending Review</MenuItem>
            <MenuItem value="OPEN" sx={{ fontSize: '0.8125rem' }}>Open</MenuItem>
            <MenuItem value="IN_PROGRESS" sx={{ fontSize: '0.8125rem' }}>In Progress</MenuItem>
            <MenuItem value="COMPLETED" sx={{ fontSize: '0.8125rem' }}>Completed</MenuItem>
            <MenuItem value="INVOICED" sx={{ fontSize: '0.8125rem' }}>Invoiced</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {filtered.length} work orders
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchWorkOrders} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canCreate && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/portal/work-orders/create')}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', ml: 0.5 }}
            >
              New Work Order
            </Button>
          )}
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>Work Order #</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {workOrders.length === 0 ? 'No work orders yet' : 'No matching work orders'}
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((wo) => {
                  const woNumber = getField(wo, 'workOrderNumber', 'work_order_number');
                  const orderDate = getField(wo, 'orderDate', 'order_date');
                  const quotationNumber = getField(wo, 'quotationNumber', 'quotation_number');
                  const grandTotal = getField(wo, 'grandTotal', 'grand_total') || 0;
                  const progressPercent = getField(wo, 'progressPercent', 'progress_percent') || 0;
                  const completedTasks = getField(wo, 'completedTasks', 'completed_tasks') || 0;
                  const totalTasks = getField(wo, 'totalTasks', 'total_tasks') || 0;

                  return (
                    <TableRow key={wo.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(wo)}>
                      <TableCell>
                        <Box sx={{ fontWeight: 600, color: 'primary.main' }}>{woNumber || '—'}</Box>
                        {quotationNumber && (
                          <Box sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>From: {quotationNumber}</Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={wo.title || ''}>
                        {wo.title || '—'}
                      </TableCell>
                      <TableCell>{formatDate(orderDate, '—')}</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={progressPercent}
                            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                          />
                          <Box sx={{ fontSize: '0.6875rem' }}>{progressPercent}%</Box>
                        </Box>
                        <Box sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                          {completedTasks} / {totalTasks} tasks
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {wo.status === 'PENDING_REVIEW' ? (
                          <Box component="span" sx={{ color: 'text.secondary' }}>Pending review</Box>
                        ) : (
                          <Box component="span" sx={{ fontWeight: 600 }}>{formatCurrency(grandTotal)}</Box>
                        )}
                      </TableCell>
                      <TableCell align="center"><StatusChip status={wo.status} /></TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Work Order">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); open(wo); }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default PortalWorkOrders;
